import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Drawer,
  TextField,
  MenuItem,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Swal from "sweetalert2";

import axiosInstance from "../../Services/axiosConfig";
import Sidebar from "../../components/CompanySidebar";
import Footer from "../../components/Footer";
import FeedbackButton from "../../components/FeedbackButton";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";
import { useThemeMode } from "../../components/ThemeModeContext";

function ManageServices() {
  const { companyId } = useParams();
  const token = localStorage.getItem("authToken");

  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState("");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentService, setCurrentService] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    duration: "",
    cooldown: "",
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const { mode } = useThemeMode();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const fetchSectors = async () => {
    try {
      const response = await axiosInstance.get(`sectors/${token}/${companyId}/`);
      if (response.data.status === "success") {
        const sectorList = Object.entries(response.data.data).map(([id, sector]) => ({
          id,
          ...sector,
        }));
        setSectors(sectorList);
      }
    } catch (error) {
      console.error("Error fetching sectors", error);
    }
  };

  const fetchServices = async (sectorId) => {
    if (!sectorId) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get(`services/${token}/${companyId}/${sectorId}/`);
      if (response.data.status === "success") {
        const serviceList = Object.entries(response.data.data).map(([id, service]) => ({
          id,
          ...service,
        }));
        setServices(serviceList);
      } else {
        setServices([]);
      }
    } catch (error) {
      console.error("Error fetching services", error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSectors();
  }, []);

  useEffect(() => {
    fetchServices(selectedSector);
  }, [selectedSector]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const endpoint = editMode
        ? `edit-service/${token}/${companyId}/${currentService.id}/`
        : `create-service/${token}/${companyId}/${selectedSector}/`;

      const response = await axiosInstance.post(endpoint, {
        ...formData,
        duration: formData.duration,
        cooldown: formData.cooldown,
      });
      if (response.data.status === "success") {
        fetchServices(selectedSector);
        setDrawerOpen(false);
      } else {
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      console.error("Error creating/editing service", error);
      Swal.fire("Error", "An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (serviceId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const response = await axiosInstance.post(
            `delete-service/${token}/${companyId}/${serviceId}/`
          );
          if (response.data.status === "success") {
            fetchServices(selectedSector);
            Swal.fire("Deleted!", "The service has been deleted.", "success");
          } else {
            Swal.fire("Error", response.data.message, "error");
          }
        } catch (error) {
          console.error("Error deleting service", error);
          Swal.fire("Error", "An error occurred. Please try again.", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="background.default">
        <Box display="flex" flexGrow={1}>
          <Sidebar companyId={companyId} />

          <Box flex={1} p={3} color="text.primary">
            <Typography variant="h4" gutterBottom>
              Manage Services
            </Typography>

            <TextField
              select
              label="Select Sector"
              value={selectedSector}
              onChange={(e) => setSelectedSector(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
            >
              {sectors.map((sector) => (
                <MenuItem key={sector.id} value={sector.id}>
                  {sector.name}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setFormData({
                  name: "",
                  description: "",
                  price: "",
                  duration: "",
                  cooldown: "",
                });
                setEditMode(false);
                setDrawerOpen(true);
              }}
              disabled={!selectedSector}
              sx={{ mb: 2 }}
            >
              Create Service
            </Button>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
                <CircularProgress />
              </Box>
            ) : (
              services.map((service) => (
                <Box key={service.id} p={2} border={1} borderColor="grey.300" borderRadius={2} mb={2}>
                  <Typography variant="h6">{service.name}</Typography>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {service.description}
                  </ReactMarkdown>
                  <Typography>Price: R${service.price}</Typography>
                  <Typography>Duration: {service.duration} minutes</Typography>
                  <Typography>Cooldown: {service.cooldown} days</Typography>
                  <Button variant="outlined" onClick={() => handleDelete(service.id)}>Delete</Button>
                </Box>
              ))
            )}
          </Box>
        </Box>

        <FeedbackButton />
        <Footer />
        <FeedbackSnackbar open={openSnackbar} handleClose={handleCloseSnackbar} message={snackbarMsg} severity={snackbarSeverity} />
      </Box>
    </ThemeProvider>
  );
}

export default ManageServices;
